//@ts-check
import React, {Component, PureComponent} from 'react';
import {arrayOf, bool, func, node, number, oneOf, shape, string} from 'prop-types';
import classNames from 'classnames';

import styles from './TabControl.scss';

/**
 * @typedef {Object} TabItem
 * @prop {React.ReactNode} label
 * @prop {React.ReactNode} pane
 */

const tabShape = {
  label: node.isRequired,
  pane: node.isRequired,
};

/**
 * @typedef {Object} TabProps
 * @prop {React.ReactNode} label
 * @prop {number} value
 * @prop {boolean} isActive
 * @prop {function(number): *} onChange
 */

/**
 * @extends {PureComponent<TabProps>}
 */
class Tab extends PureComponent {
    static propTypes = {
      label: node.isRequired,
      value: number.isRequired,
      isActive: bool.isRequired,
      onChange: func.isRequired,
    };

    onTabClick = () => this.props.onChange(this.props.value);

    render() {
      const {isActive, value, label} = this.props;
      return (
        <li className={classNames({[styles.active]: isActive}, styles.Tab)} key={value}>
          <a onClick={this.onTabClick} className={styles.tabLabel}>
            <span>{label}</span>
          </a>
        </li>
      );
    }
}

/**
 * @typedef {Object} TabControlProps
 * @prop {TabItem[]} tabs
 * @prop {React.ReactNode} controls
 * @prop {string} className
 * @prop {'light' | 'dark'} theme
 * @prop {function(number, number): *} onTabChange
 */

/**
 * @typedef {Object} TabControlState
 * @prop {number} activeTabIndex
 */

/**
 * @extends {Component<TabControlProps, TabControlState>}
 * @returns {React.ReactElement}
 */
class TabControl extends Component {
    static propTypes = {
      activeTabIndex: number,
      className: string,
      controls: node,
      tabs: arrayOf(shape(tabShape)).isRequired,
      theme: oneOf(['light', 'dark']),
      onTabChange: func,
    };

    static defaultProps = {
      activeTabIndex: 0,
      controls: undefined,
      theme: 'dark',
      onTabChange: Function.prototype,
    };

    constructor(props) {
      super(props);
      this.state = {
        activeTabIndex: props.activeTabIndex,
      };
    }

    onTabChange = activeTabIndex => {
      this.props.onTabChange(activeTabIndex, this.state.activeTabIndex);
      this.setState({activeTabIndex});
    };

    /**
     * generates the nav links for the navbar
     * @type {function(TabItem, number): React.ReactNode}
     */
    renderTab = (tab, index) => (
      <Tab
        key={index}
        label={tab.label}
        value={index}
        isActive={this.state.activeTabIndex === index}
        onChange={this.onTabChange}
      />
    );

    render() {
      const {className, tabs, theme} = this.props;
      const labels = tabs.map(this.renderTab);
      const activeTab = tabs[this.state.activeTabIndex];
      return (
        <div className={classNames(styles.TabControl, className)}>
          <nav
            className={classNames(styles.navbar, {
              [styles.dark]: theme === 'dark',
              [styles.light]: theme === 'light',
            })}
          >
            <div className={styles.tabs}>
              <ul className={styles.tabUl}>{labels}</ul>
            </div>
            {this.props.controls && <div className={styles.controls}>{this.props.controls}</div>}
          </nav>
          <div className={classNames(styles.pane, styles.viewable)}>
            <div className={styles.scrollableArea}>{activeTab && activeTab.pane}</div>
          </div>
        </div>
      );
    }
}

export default TabControl;
